import { useEffect, useMemo } from 'react';
import { Box, Card, CardContent, Grid, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import CoreReportsService from '../../../services/CoreReportsService';
import { formatMoney } from '../../../utils/currency';
import { ChartCard, ColumnChart, Legend, LineChart, SERIES_COLORS, StatTile } from './ReportCharts';
import { compact, count, formatDateTime, formatDay, periodLabel } from './reportsFormat';
import useReport from './useReport';
import ReportState from './ReportState';

const granularityNote = { day: 'por día', week: 'por semana', month: 'por mes' };

export default function OverviewTab({ days, granularity, onFreshness }) {
  const params = useMemo(() => ({ days, granularity }), [days, granularity]);
  const report = useReport((p) => CoreReportsService.overview(p), params);
  const data = report.data;

  const freshness = data?.freshness || null;
  useEffect(() => { onFreshness?.(freshness); }, [freshness, onFreshness]);

  return (
    <ReportState report={report}>
      {data && <Content data={data} granularity={granularity} />}
    </ReportState>
  );
}

function Content({ data, granularity }) {
  const { totals, series, companies } = data;
  const labels = series.map((p) => periodLabel(p.periodStart, granularity));
  const note = granularityNote[granularity];
  const peakStaff = Math.max(0, ...series.map((p) => p.activeStaffUsersPeak));
  const peakCashiers = Math.max(0, ...series.map((p) => p.activeCashierUsersPeak));
  const average = granularity !== 'day';
  const empty = series.length === 0 ? 'Todavía no hay datos para este rango.' : null;

  return (
    <Box>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={3}><StatTile label="Empresas activas" value={count(companies.active)} hint={`${count(companies.inactive)} dadas de baja`} /></Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatTile label={`Sin actividad hace más de ${companies.dormantThresholdDays} días`} value={count(companies.dormant.length)} hint="Empresas activas en riesgo de abandono" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatTile label="Ventas en pesos" value={formatMoney(totals.salesTotalUyu, 'UYU', { decimals: 0 })} hint={`${count(totals.salesCountUyu)} ventas`} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatTile label="Ventas en dólares" value={formatMoney(totals.salesTotalUsd, 'USD', { decimals: 0 })} hint={`${count(totals.salesCountUsd)} ventas`} />
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mt: 0 }}>
        <Grid item xs={12} md={6}>
          <ChartCard
            title="Ventas en pesos" subtitle={`Monto ${note}, todas las empresas`} empty={empty}
            table={{ columns: ['Período', 'Ventas', 'Monto'], rows: series.map((p, i) => [labels[i], count(p.totals.salesCountUyu), formatMoney(p.totals.salesTotalUyu, 'UYU')]) }}
          >
            <ColumnChart
              data={series.map((p, i) => ({ label: labels[i], value: Number(p.totals.salesTotalUyu), detail: `${count(p.totals.salesCountUyu)} ventas` }))}
              format={(v) => `$ ${compact(v)}`} seriesName="Monto en pesos"
            />
          </ChartCard>
        </Grid>
        <Grid item xs={12} md={6}>
          <ChartCard
            title="Ventas en dólares" subtitle={`Monto ${note}, todas las empresas (escala propia: no se mezcla con pesos)`} empty={empty}
            table={{ columns: ['Período', 'Ventas', 'Monto'], rows: series.map((p, i) => [labels[i], count(p.totals.salesCountUsd), formatMoney(p.totals.salesTotalUsd, 'USD')]) }}
          >
            <ColumnChart
              data={series.map((p, i) => ({ label: labels[i], value: Number(p.totals.salesTotalUsd), detail: `${count(p.totals.salesCountUsd)} ventas` }))}
              format={(v) => `US$ ${compact(v)}`} color={SERIES_COLORS[1]} seriesName="Monto en dólares"
            />
          </ChartCard>
        </Grid>
        <Grid item xs={12} md={6}>
          <ChartCard
            title="Usuarios activos" empty={empty}
            subtitle={average ? `Promedio de los días de cada tramo (pico: staff ${count(peakStaff)}, cajeros ${count(peakCashiers)})` : 'Personas con sesión (staff) o con ventas (cajeros) ese día'}
            legend={<Legend items={[{ name: 'Staff', color: SERIES_COLORS[0] }, { name: 'Cajeros', color: SERIES_COLORS[1] }]} />}
            table={{ columns: ['Período', 'Staff (prom.)', 'Staff (pico)', 'Cajeros (prom.)', 'Cajeros (pico)'], rows: series.map((p, i) => [labels[i], p.activeStaffUsersAvg, p.activeStaffUsersPeak, p.activeCashierUsersAvg, p.activeCashierUsersPeak]) }}
          >
            <LineChart
              labels={labels}
              series={[
                { name: 'Staff', color: SERIES_COLORS[0], values: series.map((p) => p.activeStaffUsersAvg) },
                { name: 'Cajeros', color: SERIES_COLORS[1], values: series.map((p) => p.activeCashierUsersAvg) },
              ]}
            />
          </ChartCard>
        </Grid>
        <Grid item xs={12} md={6}>
          <ChartCard
            title="Empresas nuevas por semana" subtitle="Últimas 12 semanas (semana que empieza el lunes)"
            table={{ columns: ['Semana', 'Altas'], rows: companies.newByWeek.map((w) => [formatDay(w.weekStart), w.count]) }}
          >
            <ColumnChart data={companies.newByWeek.map((w) => ({ label: periodLabel(w.weekStart, 'day'), value: w.count }))} format={(v) => String(Math.round(v))} seriesName="Altas" />
          </ChartCard>
        </Grid>
      </Grid>

      <Card variant="outlined" sx={{ mt: 2 }}>
        <CardContent>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Empresas sin actividad hace más de {companies.dormantThresholdDays} días</Typography>
          {companies.dormant.length === 0 ? (
            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>Ninguna: todas las empresas activas tuvieron movimiento en las últimas dos semanas.</Typography>
          ) : (
            <Table size="small" sx={{ mt: 1 }}>
              <TableHead><TableRow><TableCell>Empresa</TableCell><TableCell align="right">Última actividad</TableCell></TableRow></TableHead>
              <TableBody>
                {companies.dormant.map((c) => (
                  <TableRow key={c.id}>
                    <TableCell>{c.name}</TableCell>
                    <TableCell align="right">{c.lastActivityAt ? formatDateTime(c.lastActivityAt) : 'Nunca'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
